import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

type GuardContextValue = {
  // Whether change protection is enabled for the cluster currently in scope.
  enabled: boolean;
  // Show the acknowledgement dialog; resolves true if the operator confirms,
  // false if they cancel/dismiss.
  requireAck: () => Promise<boolean>;
};

// Default (no provider in the tree) = no protection, always allow.
const ChangeProtectionGuardContext = createContext<GuardContextValue>({
  enabled: false,
  requireAck: () => Promise.resolve(true),
});

export function useChangeProtectionGuardContext(): GuardContextValue {
  return useContext(ChangeProtectionGuardContext);
}

/**
 * Wrap the cluster-detail page with this provider. When `enabled` is true,
 * mutating cluster actions (intercepted in useActionWithPath) require the
 * operator to acknowledge they have permission and are within the change
 * window before the request is sent.
 */
export function ChangeProtectionGuardProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [withinWindow, setWithinWindow] = useState(false);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const requireAck = useCallback(() => {
    setHasPermission(false);
    setWithinWindow(false);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = (ok: boolean) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(ok);
  };

  const canProceed = hasPermission && withinWindow;

  return (
    <ChangeProtectionGuardContext.Provider value={{ enabled, requireAck }}>
      {children}
      <Dialog open={open} onClose={() => settle(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "error.main",
          }}
        >
          <WarningAmberRoundedIcon color="error" />
          STOP — Changes are restricted for this cluster
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This cluster does not allow any changes of any kind — even pod
            restarts — without express permission. Changes are normally only
            permitted within the agreed change window. Do NOT proceed unless you
            have been explicitly authorized.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasPermission}
                  onChange={(e) => setHasPermission(e.target.checked)}
                />
              }
              label="I have express permission to make this change."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={withinWindow}
                  onChange={(e) => setWithinWindow(e.target.checked)}
                />
              }
              label="I am making this change within the requested change window."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => settle(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!canProceed}
            onClick={() => settle(true)}
          >
            I understand — proceed
          </Button>
        </DialogActions>
      </Dialog>
    </ChangeProtectionGuardContext.Provider>
  );
}
